import React, { useEffect, useMemo, useState } from 'react';
import {
  getReports,
  generateReport,
  deleteReport,
  sendReport,
  downloadReport,
  getReportSchedules,
  createReportSchedule,
  updateReportSchedule,
  deleteReportSchedule,
  runReportSchedule,
  getSites,
} from '../services/api';
import { useTimezone } from '../context/TimezoneContext';
import { Site } from '../types';

const REPORT_TYPES = [
  { value: 'executive', label: 'Executive', detail: 'KPIs, trends, and recommendations' },
  { value: 'operational', label: 'Operational', detail: 'Device metrics, anomalies, and watch-outs' },
];

const FREQUENCIES = [
  { value: 'once', label: 'Once' },
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly', label: 'Monthly' },
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

const GENERATED_REPORT_PAGE_SIZE = 10;

function toDateTimeLocalInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  const hour = pad(date.getHours());
  const minute = pad(date.getMinutes());
  return `${year}-${month}-${day}T${hour}:${minute}`;
}

function normalizeDayOfMonth(value: string) {
  if (value.trim() === '') return 1;
  const next = Number(value);
  if (!Number.isFinite(next)) return 1;
  return Math.min(Math.max(Math.trunc(next), 1), 31);
}

export default function Reports() {
  const { formatTime } = useTimezone();

  const [type, setType] = useState<'executive' | 'operational'>('executive');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [siteId, setSiteId] = useState('');
  const [sites, setSites] = useState<Site[]>([]);
  const [generating, setGenerating] = useState(false);

  const [reports, setReports] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingSchedules, setLoadingSchedules] = useState(false);
  const [generatedReportPage, setGeneratedReportPage] = useState(0);

  const [showScheduleForm, setShowScheduleForm] = useState(false);
  const [editingScheduleId, setEditingScheduleId] = useState<string | null>(null);
  const [scheduleType, setScheduleType] = useState<'executive' | 'operational'>('executive');
  const [scheduleTitle, setScheduleTitle] = useState('');
  const [scheduleDescription, setScheduleDescription] = useState('');
  const [frequency, setFrequency] = useState('daily');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [dayOfMonth, setDayOfMonth] = useState('1');
  const [timeOfDay, setTimeOfDay] = useState('08:00');
  const [timezone, setTimezone] = useState('Africa/Lagos');
  const [recipients, setRecipients] = useState('');
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [nextRunAt, setNextRunAt] = useState('');
  const [scheduleSiteId, setScheduleSiteId] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!success && !error) return;
    const timer = setTimeout(() => {
      setSuccess('');
      setError('');
    }, 3500);
    return () => clearTimeout(timer);
  }, [success, error]);

  useEffect(() => {
    const now = new Date();
    setEnd(toDateTimeLocalInput(now));
    setStart(toDateTimeLocalInput(new Date(now.getTime() - 24 * 60 * 60 * 1000)));
    setNextRunAt(toDateTimeLocalInput(new Date(now.getTime() + 24 * 60 * 60 * 1000)));
    loadData();
    getSites().then(setSites).catch(() => {});
  }, []);

  async function loadData() {
    setLoadingReports(true);
    setLoadingSchedules(true);
    setError('');
    try {
      const [reportList, scheduleList] = await Promise.all([getReports(), getReportSchedules()]);
      setReports(reportList);
      setSchedules(scheduleList);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load reports data.');
    } finally {
      setLoadingReports(false);
      setLoadingSchedules(false);
    }
  }

  const isValidRange = useMemo(() => {
    if (!start || !end) return false;
    return new Date(start) < new Date(end);
  }, [start, end]);

  const activeScheduleCount = schedules.filter((schedule) => schedule.enabled).length;
  const recipientCount = schedules.reduce((total, schedule) => total + getRecipientList(schedule).length, 0);
  const latestReport = useMemo(
    () => [...reports].sort((a, b) => new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime())[0],
    [reports]
  );
  const generatedReportTotalPages = Math.max(1, Math.ceil(reports.length / GENERATED_REPORT_PAGE_SIZE));
  const generatedReportStart = generatedReportPage * GENERATED_REPORT_PAGE_SIZE;
  const visibleReports = reports.slice(generatedReportStart, generatedReportStart + GENERATED_REPORT_PAGE_SIZE);
  const selectedReportType = REPORT_TYPES.find((item) => item.value === type);
  const selectedSite = sites.find((site) => site.id === siteId);

  useEffect(() => {
    setGeneratedReportPage((current) => Math.min(current, generatedReportTotalPages - 1));
  }, [generatedReportTotalPages]);

  async function handleGenerate(event: React.FormEvent) {
    event.preventDefault();
    if (!title.trim() || !isValidRange) return;
    setGenerating(true);
    setError('');
    setSuccess('');
    try {
      await generateReport({
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        start: new Date(start).toISOString(),
        end: new Date(end).toISOString(),
        site_id: siteId || undefined,
      });
      setSuccess('Report generated successfully.');
      await loadData();
      setGeneratedReportPage(0);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate report.');
    } finally {
      setGenerating(false);
    }
  }

  async function handleDownload(reportId: string, reportTitle: string) {
    try {
      const blob = await downloadReport(reportId);
      const url = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportTitle.replace(/\s+/g, '_').toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to download report.');
    }
  }

  async function handleDeleteReport(id: string) {
    if (!window.confirm('Delete this report?')) return;
    setError('');
    try {
      await deleteReport(id);
      setSuccess('Report deleted.');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete report.');
    }
  }

  async function handleSendReport(id: string, emails: string[]) {
    if (emails.length === 0) return;
    setError('');
    try {
      await sendReport(id, emails);
      setSuccess('Report sent.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to send report.');
    }
  }

  function resetScheduleForm() {
    setEditingScheduleId(null);
    setScheduleType('executive');
    setScheduleTitle('');
    setScheduleDescription('');
    setFrequency('daily');
    setDayOfWeek(1);
    setDayOfMonth('1');
    setTimeOfDay('08:00');
    setTimezone('Africa/Lagos');
    setRecipients('');
    setScheduleEnabled(true);
    setScheduleSiteId('');
    const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
    setNextRunAt(toDateTimeLocalInput(tomorrow));
  }

  function editSchedule(schedule: any) {
    setEditingScheduleId(schedule.id);
    setScheduleType(schedule.type);
    setScheduleTitle(schedule.title);
    setScheduleDescription(schedule.description || '');
    setFrequency(schedule.frequency);
    setDayOfWeek(schedule.day_of_week ?? 1);
    setDayOfMonth(String(schedule.day_of_month ?? 1));
    setTimeOfDay(schedule.time_of_day ? schedule.time_of_day.slice(0, 5) : '08:00');
    setTimezone(schedule.timezone || 'Africa/Lagos');
    setRecipients(Array.isArray(schedule.recipients) ? schedule.recipients.join(', ') : '');
    setScheduleEnabled(schedule.enabled);
    setScheduleSiteId(schedule.site_id || '');
    setNextRunAt(schedule.next_run_at ? toDateTimeLocalInput(new Date(schedule.next_run_at)) : toDateTimeLocalInput(new Date()));
    setShowScheduleForm(true);
  }

  async function handleSaveSchedule(event: React.FormEvent) {
    event.preventDefault();
    const emails = recipients
      .split(',')
      .map((recipient) => recipient.trim())
      .filter((recipient) => recipient.includes('@'));
    if (!scheduleTitle.trim() || emails.length === 0) {
      setError('Schedule title and at least one valid recipient are required.');
      return;
    }

    setSavingSchedule(true);
    setError('');
    setSuccess('');

    const payload = {
      type: scheduleType,
      title: scheduleTitle.trim(),
      description: scheduleDescription.trim() || null,
      frequency,
      day_of_week: frequency === 'weekly' ? dayOfWeek : null,
      day_of_month: frequency === 'monthly' ? normalizeDayOfMonth(dayOfMonth) : null,
      time_of_day: `${timeOfDay}:00`,
      timezone,
      recipients: emails,
      enabled: scheduleEnabled,
      next_run_at: nextRunAt ? new Date(nextRunAt).toISOString() : new Date().toISOString(),
      site_id: scheduleSiteId || null,
    };

    try {
      if (editingScheduleId) {
        await updateReportSchedule(editingScheduleId, payload);
        setSuccess('Schedule updated.');
      } else {
        await createReportSchedule(payload);
        setSuccess('Schedule created.');
      }
      resetScheduleForm();
      setShowScheduleForm(false);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save schedule.');
    } finally {
      setSavingSchedule(false);
    }
  }

  async function handleDeleteSchedule(id: string) {
    if (!window.confirm('Delete this scheduled report?')) return;
    setError('');
    try {
      await deleteReportSchedule(id);
      setSuccess('Schedule deleted.');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete schedule.');
    }
  }

  async function handleRunSchedule(id: string) {
    setError('');
    try {
      await runReportSchedule(id);
      setSuccess('Schedule ran. The report will appear in the list shortly.');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to run schedule.');
    }
  }

  async function toggleScheduleEnabled(schedule: any) {
    setError('');
    try {
      await updateReportSchedule(schedule.id, { enabled: !schedule.enabled });
      setSuccess(`Schedule ${schedule.enabled ? 'paused' : 'enabled'}.`);
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update schedule.');
    }
  }

  return (
    <section className="sd-page-stack sd-reports-page">
      {(success || error) && (
        <div className="sd-report-toast-stack">
          {error && <div className="sd-toast is-error">{error}</div>}
          {success && <div className="sd-toast is-success">{success}</div>}
        </div>
      )}

      <div className="sd-page-header">
        <div className="sd-heading">
          <div className="sd-eyebrow">REPORTING</div>
          <h1>Reports</h1>
          <p>Generate PDFs, schedule delivery, and keep operational snapshots ready for review.</p>
        </div>
        <div className="sd-page-actions">
          <button className="sd-secondary-button" onClick={loadData} type="button">
            Refresh
          </button>
          <button
            className="sd-primary-button"
            onClick={() => {
              resetScheduleForm();
              setShowScheduleForm(true);
            }}
            type="button"
          >
            New Schedule
          </button>
        </div>
      </div>

      <section className="sd-kpi-grid" aria-label="Report metrics">
        <article className="sd-kpi-card">
          <div className="sd-kpi-label">PDF LIBRARY</div>
          <div className="sd-kpi-value">{reports.length}</div>
          <div className="sd-kpi-detail">{loadingReports ? 'Refreshing' : 'Generated reports'}</div>
          <div className="sd-kpi-footer"><span>On-demand archive</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-success">
          <div className="sd-kpi-label">ACTIVE SCHEDULES</div>
          <div className="sd-kpi-value">{activeScheduleCount}<span>/{Math.max(schedules.length, 1)}</span></div>
          <div className="sd-kpi-detail">{schedules.length - activeScheduleCount} paused</div>
          <div className="sd-kpi-footer"><span>Delivery automation</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-warning">
          <div className="sd-kpi-label">RECIPIENTS</div>
          <div className="sd-kpi-value">{recipientCount}</div>
          <div className="sd-kpi-detail">Across scheduled reports</div>
          <div className="sd-kpi-footer"><span>Email distribution</span></div>
        </article>
        <article className="sd-kpi-card sd-kpi-blue">
          <div className="sd-kpi-label">LATEST REPORT</div>
          <div className="sd-kpi-value sd-report-latest-value">{latestReport ? formatTime(latestReport.created_at) : '-'}</div>
          <div className="sd-kpi-detail">{latestReport?.title || 'No report generated yet'}</div>
          <div className="sd-kpi-footer"><span>{latestReport?.type || 'Awaiting first PDF'}</span></div>
        </article>
      </section>

      <section className="sd-reports-workspace">
        <form className="sd-panel sd-report-builder" onSubmit={handleGenerate}>
          <div className="sd-panel-header">
            <div>
              <h2>PDF Builder</h2>
              <p>{selectedReportType?.detail}</p>
            </div>
            <span className="sd-status-label is-online"><i />manual</span>
          </div>
          <div className="sd-report-form-body">
            <div className="sd-form-grid">
              <label className="sd-field">
                <span>Report Type</span>
                <select value={type} onChange={(event) => setType(event.target.value as any)}>
                  {REPORT_TYPES.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
                <small>Choose the PDF template and level of operational detail.</small>
              </label>
              {sites.length > 0 && (
                <label className="sd-field">
                  <span>Site</span>
                  <select value={siteId} onChange={(event) => setSiteId(event.target.value)}>
                    <option value="">All Sites</option>
                    {sites.map((site) => (
                      <option key={site.id} value={site.id}>{site.name}</option>
                    ))}
                  </select>
                  <small>Limit the report to one site, or keep All Sites for fleet-wide reporting.</small>
                </label>
              )}
              <label className="sd-field sd-form-span-2">
                <span>Title</span>
                <input
                  placeholder="e.g. Weekly Operations Review"
                  value={title}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                />
                <small>Title shown on the PDF cover and generated report list.</small>
              </label>
            </div>
            <label className="sd-field">
              <span>Description</span>
              <input
                placeholder="e.g. Include generator runtime, alarms, and export summary"
                value={description}
                onChange={(event) => setDescription(event.target.value)}
              />
              <small>Optional note printed in the report summary section.</small>
            </label>
            <div className="sd-form-grid">
              <label className="sd-field">
                <span>From</span>
                <input type="datetime-local" value={start} onChange={(event) => setStart(event.target.value)} required />
                <small>Start of the telemetry and alert window.</small>
              </label>
              <label className="sd-field">
                <span>To</span>
                <input type="datetime-local" value={end} onChange={(event) => setEnd(event.target.value)} required />
                <small>End of the reporting window. Must be after From.</small>
              </label>
            </div>
            {!isValidRange && start && end && (
              <div className="sd-inline-alert is-warning">The To time must be after the From time.</div>
            )}
            <div className="sd-report-build-summary">
              <SummaryFact label="Scope" value={selectedSite?.name || 'All Sites'} />
              <SummaryFact label="Format" value="PDF" />
              <SummaryFact label="Window" value={start && end ? `${formatShortDate(start)} to ${formatShortDate(end)}` : 'Select range'} />
            </div>
            <button
              className="sd-primary-button"
              disabled={generating || !isValidRange || !title.trim()}
              type="submit"
            >
              {generating ? 'Generating PDF...' : 'Generate PDF'}
            </button>
          </div>
        </form>

        <article className="sd-panel sd-report-schedule-panel">
          <div className="sd-panel-header">
            <div>
              <h2>Schedule Editor</h2>
              <p>Create automated report delivery for operations teams.</p>
            </div>
            <button
              className="sd-link-button"
              onClick={() => {
                resetScheduleForm();
                setShowScheduleForm((visible) => !visible);
              }}
              type="button"
            >
              {showScheduleForm ? 'Close' : 'Open'}
            </button>
          </div>

          {showScheduleForm ? (
            <form className="sd-report-schedule-form" onSubmit={handleSaveSchedule}>
              <div className="sd-form-grid">
                <label className="sd-field">
                  <span>Type</span>
                  <select value={scheduleType} onChange={(event) => setScheduleType(event.target.value as any)}>
                    {REPORT_TYPES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <small>PDF template sent by this schedule.</small>
                </label>
                {sites.length > 0 && (
                  <label className="sd-field">
                    <span>Site</span>
                    <select value={scheduleSiteId} onChange={(event) => setScheduleSiteId(event.target.value)}>
                      <option value="">All Sites</option>
                      {sites.map((site) => (
                        <option key={site.id} value={site.id}>{site.name}</option>
                      ))}
                    </select>
                    <small>Scope scheduled reports to one site or all tenant sites.</small>
                  </label>
                )}
                <label className="sd-field sd-form-span-2">
                  <span>Title</span>
                  <input
                    placeholder="e.g. Daily Executive Summary"
                    value={scheduleTitle}
                    onChange={(event) => setScheduleTitle(event.target.value)}
                    required
                  />
                  <small>Reusable title for every PDF generated by this schedule.</small>
                </label>
                <label className="sd-field sd-form-span-2">
                  <span>Description</span>
                  <input
                    placeholder="e.g. Auto-send alarms and uptime summary to operations leads"
                    value={scheduleDescription}
                    onChange={(event) => setScheduleDescription(event.target.value)}
                  />
                  <small>Optional delivery context for administrators reviewing schedules.</small>
                </label>
                <label className="sd-field">
                  <span>Frequency</span>
                  <select value={frequency} onChange={(event) => setFrequency(event.target.value)}>
                    {FREQUENCIES.map((item) => (
                      <option key={item.value} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </select>
                  <small>How often Seedronics should generate and email the report.</small>
                </label>
                {frequency === 'weekly' && (
                  <label className="sd-field">
                    <span>Day of Week</span>
                    <select value={dayOfWeek} onChange={(event) => setDayOfWeek(Number(event.target.value))}>
                      {DAYS_OF_WEEK.map((item) => (
                        <option key={item.value} value={item.value}>
                          {item.label}
                        </option>
                      ))}
                    </select>
                    <small>Weekly delivery day in the selected timezone.</small>
                  </label>
                )}
                {frequency === 'monthly' && (
                  <label className="sd-field">
                    <span>Day of Month</span>
                    <input
                      placeholder="e.g. 1"
                      type="number"
                      min={1}
                      max={31}
                      value={dayOfMonth}
                      onChange={(event) => setDayOfMonth(event.target.value)}
                    />
                    <small>Monthly delivery day. Values are clamped between 1 and 31.</small>
                  </label>
                )}
                <label className="sd-field">
                  <span>Time</span>
                  <input type="time" value={timeOfDay} onChange={(event) => setTimeOfDay(event.target.value)} required />
                  <small>Local time of day for scheduled generation.</small>
                </label>
                <label className="sd-field">
                  <span>Timezone</span>
                  <input
                    placeholder="e.g. Africa/Lagos"
                    value={timezone}
                    onChange={(event) => setTimezone(event.target.value)}
                    required
                  />
                  <small>IANA timezone used to calculate run times.</small>
                </label>
                <label className="sd-field sd-form-span-2">
                  <span>Recipients</span>
                  <input
                    placeholder="e.g. ops@company.com, manager@company.com"
                    value={recipients}
                    onChange={(event) => setRecipients(event.target.value)}
                    required
                  />
                  <small>Comma-separated email list. At least one valid address is required.</small>
                </label>
                <label className="sd-field">
                  <span>First Run At</span>
                  <input type="datetime-local" value={nextRunAt} onChange={(event) => setNextRunAt(event.target.value)} required />
                  <small>Initial run time. Future runs follow the frequency settings.</small>
                </label>
                <label className="sd-checkbox-card">
                  <input type="checkbox" checked={scheduleEnabled} onChange={(event) => setScheduleEnabled(event.target.checked)} />
                  <span>
                    <strong>Enabled</strong>
                    <small>Allow this schedule to run automatically.</small>
                  </span>
                </label>
              </div>
              <div className="sd-drawer-actions sd-report-schedule-actions">
                <button
                  onClick={() => {
                    resetScheduleForm();
                    setShowScheduleForm(false);
                  }}
                  type="button"
                >
                  Cancel
                </button>
                <button disabled={savingSchedule} type="submit">
                  {savingSchedule ? 'Saving...' : editingScheduleId ? 'Update Schedule' : 'Create Schedule'}
                </button>
              </div>
            </form>
          ) : (
            <div className="sd-report-schedule-empty">
              <strong>{activeScheduleCount} active schedules</strong>
              <span>Open the editor to create or update automated PDF delivery.</span>
            </div>
          )}
        </article>
      </section>

      <section className="sd-report-lists-grid">
        <article className="sd-panel">
          <div className="sd-panel-header">
            <div>
              <h2>Generated Reports</h2>
              <p>Download, send, or remove generated PDF snapshots.</p>
            </div>
          </div>
          <div className="sd-table-wrap sd-report-table-wrap">
            <table className="sd-table sd-report-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Type</th>
                  <th>Period</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingReports ? (
                  <tr><td colSpan={5} className="sd-empty-row">Loading reports...</td></tr>
                ) : reports.length === 0 ? (
                  <tr><td colSpan={5} className="sd-empty-row">No reports generated yet.</td></tr>
                ) : (
                  visibleReports.map((report) => (
                    <tr key={report.id}>
                      <td>
                        <strong>{report.title}</strong>
                        {report.description && <span>{report.description}</span>}
                      </td>
                      <td>
                        <span className={`sd-status-label ${report.type === 'executive' ? 'is-online' : 'is-maintenance'}`}>
                          <i />
                          {report.type}
                        </span>
                      </td>
                      <td>{formatTime(report.start_date)} to {formatTime(report.end_date)}</td>
                      <td>{formatTime(report.created_at)}</td>
                      <td>
                        <div className="sd-row-actions">
                          <button onClick={() => handleDownload(report.id, report.title)} type="button">Download</button>
                          <button
                            onClick={() => {
                              const emails = window.prompt('Send to comma-separated emails:');
                              if (emails) handleSendReport(report.id, emails.split(',').map((email) => email.trim()).filter((email) => email.includes('@')));
                            }}
                            type="button"
                          >
                            Send
                          </button>
                          <button className="is-danger" onClick={() => handleDeleteReport(report.id)} type="button">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="sd-report-card-list sd-mobile-record-list">
            {loadingReports ? (
              <div className="sd-mobile-empty-card">Loading reports...</div>
            ) : reports.length === 0 ? (
              <div className="sd-mobile-empty-card">No reports generated yet.</div>
            ) : (
              visibleReports.map((report) => (
                <article className="sd-mobile-record-card" key={report.id}>
                  <header className="sd-mobile-record-header">
                    <span className={`sd-status-label ${report.type === 'executive' ? 'is-online' : 'is-maintenance'}`}>
                      <i />
                      {report.type}
                    </span>
                    <time>{formatTime(report.created_at)}</time>
                  </header>
                  <div className="sd-mobile-record-title">
                    <strong>{report.title}</strong>
                    {report.description && <span>{report.description}</span>}
                  </div>
                  <div className="sd-mobile-record-fact">
                    <span>Period</span>
                    <strong>{formatTime(report.start_date)} to {formatTime(report.end_date)}</strong>
                  </div>
                  <div className="sd-mobile-record-actions">
                    <button onClick={() => handleDownload(report.id, report.title)} type="button">Download</button>
                    <button
                      onClick={() => {
                        const emails = window.prompt('Send to comma-separated emails:');
                        if (emails) handleSendReport(report.id, emails.split(',').map((email) => email.trim()).filter((email) => email.includes('@')));
                      }}
                      type="button"
                    >
                      Send
                    </button>
                    <button className="is-danger" onClick={() => handleDeleteReport(report.id)} type="button">Delete</button>
                  </div>
                </article>
              ))
            )}
          </div>
          {reports.length > GENERATED_REPORT_PAGE_SIZE && (
            <div className="sd-pagination-footer">
              <span>
                Showing {generatedReportStart + 1}-{Math.min(generatedReportStart + GENERATED_REPORT_PAGE_SIZE, reports.length)} of {reports.length}
              </span>
              <div className="sd-row-actions">
                <button
                  disabled={generatedReportPage === 0}
                  onClick={() => setGeneratedReportPage((page) => Math.max(0, page - 1))}
                  type="button"
                >
                  Previous
                </button>
                <button
                  disabled={generatedReportPage >= generatedReportTotalPages - 1}
                  onClick={() => setGeneratedReportPage((page) => Math.min(generatedReportTotalPages - 1, page + 1))}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </article>

        <article className="sd-panel">
          <div className="sd-panel-header">
            <div>
              <h2>Scheduled Reports</h2>
              <p>Run now, edit cadence, pause delivery, or delete schedules.</p>
            </div>
          </div>
          <div className="sd-table-wrap sd-report-schedule-table-wrap">
            <table className="sd-table sd-report-schedule-table">
              <thead>
                <tr>
                  <th>Schedule</th>
                  <th>Status</th>
                  <th>Cadence</th>
                  <th>Recipients</th>
                  <th>Next Run</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {loadingSchedules ? (
                  <tr><td colSpan={6} className="sd-empty-row">Loading schedules...</td></tr>
                ) : schedules.length === 0 ? (
                  <tr><td colSpan={6} className="sd-empty-row">No scheduled reports yet.</td></tr>
                ) : (
                  schedules.map((schedule) => (
                    <tr key={schedule.id}>
                      <td>
                        <strong>{schedule.title}</strong>
                        <span>{schedule.site_name || 'All Sites'} / {schedule.type}</span>
                      </td>
                      <td>
                        <span className={`sd-status-label ${schedule.enabled ? 'is-online' : 'is-offline'}`}>
                          <i />
                          {schedule.enabled ? 'enabled' : 'paused'}
                        </span>
                      </td>
                      <td>{formatScheduleCadence(schedule)}</td>
                      <td>{getRecipientList(schedule).join(', ') || '-'}</td>
                      <td>{schedule.next_run_at ? formatTime(schedule.next_run_at) : '-'}</td>
                      <td>
                        <div className="sd-row-actions">
                          <button onClick={() => handleRunSchedule(schedule.id)} type="button">Run</button>
                          <button onClick={() => editSchedule(schedule)} type="button">Edit</button>
                          <button onClick={() => toggleScheduleEnabled(schedule)} type="button">
                            {schedule.enabled ? 'Pause' : 'Enable'}
                          </button>
                          <button className="is-danger" onClick={() => handleDeleteSchedule(schedule.id)} type="button">Delete</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className="sd-report-schedule-card-list sd-mobile-record-list">
            {loadingSchedules ? (
              <div className="sd-mobile-empty-card">Loading schedules...</div>
            ) : schedules.length === 0 ? (
              <div className="sd-mobile-empty-card">No scheduled reports yet.</div>
            ) : (
              schedules.map((schedule) => (
                <article className="sd-mobile-record-card" key={schedule.id}>
                  <header className="sd-mobile-record-header">
                    <span className={`sd-status-label ${schedule.enabled ? 'is-online' : 'is-offline'}`}>
                      <i />
                      {schedule.enabled ? 'enabled' : 'paused'}
                    </span>
                    <time>{schedule.next_run_at ? formatTime(schedule.next_run_at) : 'No next run'}</time>
                  </header>
                  <div className="sd-mobile-record-title">
                    <strong>{schedule.title}</strong>
                    <span>{schedule.site_name || 'All Sites'} / {schedule.type}</span>
                  </div>
                  <div className="sd-mobile-record-grid">
                    <div className="sd-mobile-record-fact">
                      <span>Cadence</span>
                      <strong>{formatScheduleCadence(schedule)}</strong>
                    </div>
                    <div className="sd-mobile-record-fact">
                      <span>Recipients</span>
                      <strong>{getRecipientList(schedule).join(', ') || '-'}</strong>
                    </div>
                  </div>
                  <div className="sd-mobile-record-actions">
                    <button onClick={() => handleRunSchedule(schedule.id)} type="button">Run</button>
                    <button onClick={() => editSchedule(schedule)} type="button">Edit</button>
                    <button onClick={() => toggleScheduleEnabled(schedule)} type="button">
                      {schedule.enabled ? 'Pause' : 'Enable'}
                    </button>
                    <button className="is-danger" onClick={() => handleDeleteSchedule(schedule.id)} type="button">Delete</button>
                  </div>
                </article>
              ))
            )}
          </div>
        </article>
      </section>
    </section>
  );
}

function SummaryFact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function getRecipientList(schedule: any): string[] {
  if (Array.isArray(schedule.recipients)) return schedule.recipients;
  if (typeof schedule.recipients === 'string') {
    return schedule.recipients.split(',').map((recipient: string) => recipient.trim()).filter(Boolean);
  }
  return [];
}

function formatScheduleCadence(schedule: any): string {
  if (schedule.frequency === 'weekly') {
    const day = DAYS_OF_WEEK.find((item) => item.value === schedule.day_of_week)?.label || 'Weekly';
    return `${day} at ${String(schedule.time_of_day || '').slice(0, 5) || '--:--'}`;
  }
  if (schedule.frequency === 'monthly') {
    return `Day ${schedule.day_of_month || 1} at ${String(schedule.time_of_day || '').slice(0, 5) || '--:--'}`;
  }
  return `${schedule.frequency || 'daily'} at ${String(schedule.time_of_day || '').slice(0, 5) || '--:--'}`;
}

function formatShortDate(value: string): string {
  if (!value) return '-';
  return new Date(value).toLocaleDateString([], { month: 'short', day: 'numeric' });
}
